// Imports for external libraries go here.
import React, { FC, useCallback, useState } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { Button, CheckBox, Link, getEventKeyTypes } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { LegalTextWithLink } from '../LegalTextWithLink';
import { LinkAccordionBody } from '../TermModalBody';
import { StyledConsentCheckListItem } from './ConsentCheckListItem.styles';
import { ConsentCheckListItemProps } from './ConsentCheckListItem.types';

export const ConsentCheckListItem: FC<ConsentCheckListItemProps> = props => {
  const {
    label,
    errorLabel,
    termsBtnLabel,
    termsLink,
    selectedConsentId,
    id,
    checkboxLabelContainerClassName,
    checkboxInnerLabelClassName,
    checkboxContainerClassName,
    className,
    name,
    isChecked,
    onModalOpen,
    onChange,
    onLinkClickInCheckbox,
    linkClass,
    linkTarget,
    errorMessageClassName,
    validateErrors,
    showCheckbox,
    linkBehavior,
    closeAccordionBtnLabel,
    openAccordionBtnLabel,
  } = props;
  const [accordionOpen, setAccordionOpen] = useState(false);

  const showError = validateErrors && !isChecked;

  const onCheckboxChange = () => {
    onChange(selectedConsentId, id);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLLabelElement>) => {
    if (getEventKeyTypes(event)?.isEnterKey && event.currentTarget instanceof HTMLInputElement) {
      event.preventDefault();
      onCheckboxChange();
    }
  };

  const onTermsModalOpen = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      onModalOpen(e, termsLink!);
    },
    [termsLink, onModalOpen] // Dependencies to ensure function isn't recreated unnecessarily
  );

  const handleOpenAccordion = () => setAccordionOpen(true);
  const handleCloseAccordion = () => setAccordionOpen(false);

  const isDefaultTermsLink = termsLink && termsBtnLabel && linkBehavior === 'default';
  const shouldDisplayShowMoreButton = termsLink && linkBehavior === 'accordion' && !accordionOpen;

  return (
    <>
      <StyledConsentCheckListItem
        onClick={onLinkClickInCheckbox} // Attach click handler to all the anchor tags present within the checkbox label
        data-testid="legal-ConsentCheckListItem"
        data-component-name="m-legal-ConsentCheckListItem"
        className={checkboxContainerClassName ?? ''}
      >
        {showCheckbox ? (
          <CheckBox
            checkboxId={id}
            checkboxName={name}
            className={className ? className : ''}
            checked={isChecked}
            onChange={onCheckboxChange}
            onKeyDown={handleKeyDown}
          >
            <LegalTextWithLink
              descriptionText={label}
              className={checkboxLabelContainerClassName || ''}
              innerChildClassName={checkboxInnerLabelClassName || ''}
            />
            {showError && errorLabel && (
              <LegalTextWithLink descriptionText={errorLabel} className={errorMessageClassName} />
            )}
          </CheckBox>
        ) : (
          <>
            <LegalTextWithLink
              descriptionText={label}
              className={checkboxLabelContainerClassName || ''}
              innerChildClassName={checkboxInnerLabelClassName || ''}
            />
            {showError && errorLabel && (
              <LegalTextWithLink descriptionText={errorLabel} className={errorMessageClassName} />
            )}
          </>
        )}
        {isDefaultTermsLink && (
          <Link
            linkHref={termsLink}
            linkClassName={linkClass ? linkClass : ''}
            target={linkTarget}
            text={termsBtnLabel}
            callback={onTermsModalOpen}
          />
        )}
        {shouldDisplayShowMoreButton && (
          <Button className={clsx('t-font-s t-display-1 term-link-accordion-button')} callback={handleOpenAccordion}>
            {openAccordionBtnLabel}
          </Button>
        )}
      </StyledConsentCheckListItem>
      {accordionOpen && (
        <LinkAccordionBody
          closeAccordionBtnLabel={closeAccordionBtnLabel}
          href={termsLink!}
          handleClose={handleCloseAccordion}
        />
      )}
    </>
  );
};
